import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import axios from '../../config/axios';

const schema = yup.object().shape({
  email: yup.string().email('Invalid email format').required('Email is required'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
});

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthenticating(true);
      const timer = setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/login.php', { username: data.email, password: data.password });
      if (response?.data?.success && response?.data?.data?.token) {
        localStorage.setItem('token', response.data.data.token);
        setAuthenticating(true);
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 3000);
      } else {
        setError(response?.data?.error || response?.data?.message || 'Login failed. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.96 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, delay: 0.15, ease: 'easeOut' },
    },
  };

  // Random positions and animation params for floating logos (stable per mount)
  const floatingLogos = useMemo(() => {
    const count = 42;
    return Array.from({ length: count }, (_, i) => ({
      left: (i * 11 + 2) % 96,
      top: (i * 19 + 5) % 93,
      size: 32 + (i % 6) * 12,
      duration: 12 + (i % 8),
      delay: (i * 0.6) % 5,
    }));
  }, []);

  return (
    <>
      <Helmet>
        <title>Login - Ishqnama Art Dashboard</title>
        <meta name="description" content="Login to your Ishqnama Art dashboard to manage orders and view business statistics." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(165deg, #2d0f1a 0%, #4a1a2a 25%, #6b2840 50%, #8b3555 75%, #5c2438 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Randomly placed floating logo background with animation */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: 'none',
            overflow: 'hidden',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
              '25%': { transform: 'translate(8px, -12px) rotate(2deg)' },
              '50%': { transform: 'translate(-10px, 8px) rotate(-1deg)' },
              '75%': { transform: 'translate(6px, 10px) rotate(1deg)' },
            },
          }}
        >
          {floatingLogos.map((logo, i) => (
            <Box
              key={i}
              component="img"
              src="/namelogo.png"
              alt=""
              sx={{
                position: 'absolute',
                left: `${logo.left}%`,
                top: `${logo.top}%`,
                width: logo.size * 1.5, // Make wordmark slightly larger to compensate for aspect ratio
                height: 'auto',
                opacity: 0.1,
                animation: 'float ease-in-out infinite',
                animationDuration: `${logo.duration}s`,
                animationDelay: `${logo.delay}s`,
              }}
            />
          ))}
        </Box>

        {/* Pink gradient orbs - more visible */}
        <Box
          sx={{
            position: 'absolute',
            top: '-15%',
            right: '-5%',
            width: 450,
            height: 450,
            background: 'radial-gradient(circle, rgba(255, 107, 157, 0.35) 0%, rgba(233, 30, 99, 0.15) 40%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '-5%',
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(233, 30, 99, 0.3) 0%, rgba(255, 107, 157, 0.12) 45%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 0,
          }}
        />

        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <motion.div variants={cardVariants} initial="hidden" animate="visible">
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(30px)',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 40px 100px rgba(0, 0, 0, 0.2)',
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ px: { xs: 3, sm: 4 }, py: 3 }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                      component="img"
                      src="/pink_logo.png"
                      alt="Ishqnama Logo"
                      sx={{
                        height: 60,
                        objectFit: 'contain',
                        display: 'block',
                        mx: 'auto',
                        mb: 2,
                      }}
                    />
                    <Box
                      component="img"
                      src="/namelogo.png"
                      alt="Ishqnama"
                      sx={{
                        height: 38,
                        objectFit: 'contain',
                        display: 'block',
                        mx: 'auto',
                        mb: 4,
                      }}
                    />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      Admin Portal
                    </Typography>
                  </Box>
                  {authenticating ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                      <Box
                        component="img"
                        src="/pink_logo.png"
                        alt="Ishqnama Logo"
                        sx={{
                          height: 64,
                          objectFit: 'contain',
                          display: 'block',
                          mx: 'auto',
                          mb: 3,
                          animation: 'pulse 1.5s infinite ease-in-out',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                            '50%': { opacity: 0.8, transform: 'scale(1.06)' },
                          },
                        }}
                      />
                      <CircularProgress size={38} sx={{ color: 'primary.main', mb: 3 }} />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                        Authenticating…
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Redirecting to dashboard…
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.25 }}
                        >
                          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                            {error}
                          </Alert>
                        </motion.div>
                      )}

                      <form onSubmit={handleSubmit(onSubmit)}>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 4,
                            fontWeight: 800,
                            color: 'text.primary',
                            textAlign: 'center',
                            fontSize: '1.25rem',
                          }}
                        >
                          Welcome back, Ishqnama Art!
                        </Typography>

                        <TextField
                          {...register('email')}
                          fullWidth
                          label="Email"
                          type="email"
                          variant="outlined"
                          error={!!errors.email}
                          helperText={errors.email?.message}
                          sx={{
                            mb: 3,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '14px',
                              backgroundColor: 'rgba(0,0,0,0.02)',
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          {...register('password')}
                          fullWidth
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          variant="outlined"
                          error={!!errors.password}
                          helperText={errors.password?.message}
                          sx={{
                            mb: 4,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '14px',
                              backgroundColor: 'rgba(0,0,0,0.02)',
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LockIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  size="small"
                                  sx={{ color: 'text.secondary' }}
                                >
                                  {showPassword ? <VisibilityOffIcon sx={{ fontSize: 20 }} /> : <VisibilityIcon sx={{ fontSize: 20 }} />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          disabled={loading}
                          sx={{
                            py: 2,
                            fontSize: '1rem',
                            fontWeight: 800,
                            borderRadius: '14px',
                            background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)', // Gradient for premium look
                            color: '#FFFFFF !important', // Explicitly white as requested
                            boxShadow: '0 8px 25px rgba(181, 29, 110, 0.25)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)', // Reversed gradient or darker shade
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 35px rgba(181, 29, 110, 0.35)',
                            },
                            '&:disabled': {
                              background: 'rgba(0,0,0,0.1)',
                              color: 'text.disabled',
                            },
                          }}
                        >
                          {loading ? 'Authenticating…' : 'Sign in'}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default Login; 